import { useEffect, useRef, useState, useCallback } from 'react';
import { AudioLines, Loader2 } from "lucide-react";
import { motion } from 'framer-motion';


import { useNotes } from "../contexts/NotesContext"
import { useCommunication } from '@/lib/CommunicationWrapper';

const formatTime = (timestamp: number) => {
    const seconds = Math.floor(timestamp / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    const secondsString = (seconds % 60).toString().padStart(2, '0');
    const minutesString = (minutes % 60).toString().padStart(2, '0');
    return `${minutesString}:${secondsString}`;
}

const TranscriptItem = ({ timestamp, text, animate, setLastIndex, index, totalItems }: {
    timestamp: number,
    text: string,
    animate: boolean,
    setLastIndex: any,
    index: number,
    totalItems: number
}) => {
    const [visibleText, setVisibleText] = useState(animate ? "" : text);
    const [opacity, setOpacity] = useState(animate ? 0 : 1);
    const timeoutRef = useRef<any>(null);

    const renderNextLetter = useCallback(() => {
        // Fade in the container first
        setOpacity(1);

        // Then start typing animation
        for (let i = 0; i < text.length; i++) {
            setTimeout(() => {
                setVisibleText(text.substring(0, i + 1));
            }, i * 30);
        }

        // When done, trigger next item if not last
        setTimeout(() => {
            if (index < totalItems - 1) {
                setLastIndex((prev: number) => prev + 1);
            }
        }, text.length * 30 + 300); // Add small delay after typing
    }, [text, index, totalItems, setLastIndex]);

    useEffect(() => {
        if (animate) {
            renderNextLetter();
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    }, [animate]);

    return (
        <motion.div
            className="rounded-lg flex gap-[10px]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: opacity, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <p className="text-sm mb-2 min-w-[60px] text-theme-0">{formatTime(timestamp)}</p>
            <p className="text-sm flex-1">
                {visibleText}
            </p>
        </motion.div>
    );
};

/*
const TranscriptContent = () => {
    const { transcript, noteId, setTranscript, activeTab, isRecording, settings, audioContextRef, analyserRef, animationFrameRef, waveformHistoryRef, maxHistoryLength } = useNotes();
    const [lastIndex, setLastIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const lastTranscriptLength = useRef<number>(0);

    // Audio waveform visualization
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const smoothScrollX = useRef<number>(0); // Track smooth scroll position

    const drawWaveform = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Set up high DPI canvas for crisp rendering
        const dpr = window.devicePixelRatio || 1;
        const displayWidth = 200;
        const displayHeight = 30;

        // Only resize if needed (to avoid constant resizing)
        if (canvas.width !== displayWidth * dpr || canvas.height !== displayHeight * dpr) {
            canvas.width = displayWidth * dpr;
            canvas.height = displayHeight * dpr;
            canvas.style.width = displayWidth + 'px';
            canvas.style.height = displayHeight + 'px';
            ctx.scale(dpr, dpr);
        }

        // Clear canvas completely (transparent background)
        ctx.clearRect(0, 0, displayWidth, displayHeight);

        if (!analyserRef.current) {
            // Draw a simple test pattern when no analyser
            ctx.strokeStyle = 'currentColor';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(0, displayHeight / 2);
            ctx.lineTo(displayWidth, displayHeight / 2);
            ctx.stroke();

            ctx.fillStyle = '#ef4444';
            ctx.font = '12px Arial';
            ctx.fillText('No Audio Analysis', 10, displayHeight / 2 - 5);
        } else {
            // Get current audio data and calculate average amplitude
            const bufferLength = analyserRef.current.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            analyserRef.current.getByteTimeDomainData(dataArray);

            // Calculate average amplitude for this frame
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
                const normalized = (dataArray[i] - 128) / 128;
                sum += Math.abs(normalized);
            }
            const avgAmplitude = sum / bufferLength;

            // Add to history with timestamp for fade-in effect
            const now = Date.now();
            waveformHistoryRef.current.push({
                amplitude: avgAmplitude,
                timestamp: now
            });

            // Don't limit history length - let it grow until transcript resets
            // (We'll reset it when transcript updates instead)

            // Draw waveform history
            const history = waveformHistoryRef.current;
            if (history.length > 1) {

                // Use fixed pixel spacing (6 pixels per data point for clear separation)
                const pixelsPerPoint = 6;

                // Calculate target starting position (start from left, scroll when exceeds canvas width)
                const totalWidth = history.length * pixelsPerPoint;
                const targetX = totalWidth <= displayWidth ? 0 : -(totalWidth - displayWidth);

                // Smooth interpolation towards target position (lerp with 0.15 factor for smooth movement)
                const lerpFactor = 0.2;
                smoothScrollX.current += (targetX - smoothScrollX.current) * lerpFactor;
                const startX = smoothScrollX.current;

                for (let i = 0; i < history.length; i++) {
                    const x = startX + (i * pixelsPerPoint);

                    // Only draw if within canvas bounds
                    if (x >= 0 && x <= displayWidth) {
                        const historyItem = history[i];
                        const amplitude = historyItem.amplitude;

                        // Draw waveform as ovals with direct audio feedback
                        const centerY = displayHeight / 2;
                        const height = Math.max(2, amplitude * displayHeight * 4); // Use full canvas height
                        const width = 3; // Fixed width for oval shape

                        // Add fade-in effect for new items (fade in over 300ms)
                        const age = now - historyItem.timestamp;
                        const fadeInDuration = 300; // milliseconds
                        const fadeInAmount = Math.min(1, age / fadeInDuration);

                        // Only apply fade-in effect, keep old items at full opacity
                        const alpha = fadeInAmount;

                        ctx.globalAlpha = alpha;
                        ctx.fillStyle = '#10b981'; // Green color for rounded squares
                        ctx.beginPath();
                        // Draw rounded rectangle centered at (x, centerY)
                        const rectX = x - width / 2;
                        const rectY = centerY - height / 2;
                        ctx.roundRect(rectX, rectY, width, height, 2); // 1px border radius
                        ctx.fill();
                    }
                }


            }
        }
    };

    // Draw waveform every 50ms when recording
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (isRecording) {
            // Reset tracking and waveform when recording starts
            lastTranscriptLength.current = transcript.length;
            waveformHistoryRef.current = []; // Clear any previous waveform data
            smoothScrollX.current = 0; // Reset smooth scroll position
            console.log('🎵 Recording started, cleared waveform history');

            interval = setInterval(() => {
                // Only draw if analyser is ready
                if (analyserRef.current) {
                    drawWaveform();
                }
            }, 100);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isRecording]);

    useEffect(() => {
        if (transcript.length > 0) return;

        const getTranscriptContent = async () => {

            const { client, error: clientCreateError } = await authenticatedClient();
            if (!client) {
                console.error("Error creating auth client:", clientCreateError);
                return;
            }

            const { data, error } = await client.from('transcripts').select('content').eq('note_id', noteId).single();

            if (error) {
                console.error("Error fetching transcript:", error);
                return null;
            }

            setTranscript(data.content);
            setLastIndex(data.content.length - 1); // Start animation with first item
        }

        getTranscriptContent();
    }, [noteId]);

    // Reset waveform when transcript updates (every ~5 seconds)
    useEffect(() => {
        if (transcript.length > lastTranscriptLength.current) {
            console.log('🎵 Transcript updated, resetting waveform. Length:', transcript.length);
            waveformHistoryRef.current = [];
            smoothScrollX.current = 0; // Reset smooth scroll position
            lastTranscriptLength.current = transcript.length;
        }
    }, [transcript]);

    // Auto-scroll to bottom when new items are added
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [transcript]);

    if (activeTab !== "transcript") return null;

    const renderTranscript = () => {
        if (transcript.length === 0) return null;

        // First, filter out items that are only a single period
        const filteredTranscript = transcript.filter((item: any) => {
            return item.text && item.text.trim() !== '.';
        });

        // Combine items into complete sentences
        const combinedTranscript: any[] = [];
        let currentSentence = '';
        let currentStartTime = 0;
        let currentEndTime = 0;

        filteredTranscript.forEach((item: any, index: number) => {
            if (currentSentence === '') {
                // Start of new sentence
                currentStartTime = item.start || 0;
            }

            currentSentence += (currentSentence ? ' ' : '') + item.text;
            currentEndTime = item.end || 0;

            // Check if this item ends with a period (end of sentence)
            if (item.text.trim().endsWith('.') || index === filteredTranscript.length - 1) {
                combinedTranscript.push({
                    text: currentSentence,
                    start: currentStartTime,
                    end: currentEndTime
                });
                currentSentence = '';
            }
        });

        // Calculate cumulative timestamps for combined sentences
        let cumulativeTime = 0;
        const transcriptWithTimestamps = combinedTranscript.map((item: any) => {
            cumulativeTime += item.end * 1000; // Convert to milliseconds
            return {
                ...item,
                cumulativeTime
            };
        });

        return transcriptWithTimestamps.map((item: any, index: number) => (
            <TranscriptItem
                timestamp={item.cumulativeTime}
                text={item.text}
                animate={index === lastIndex}
                setLastIndex={setLastIndex}
                index={index}
                totalItems={transcriptWithTimestamps.length}
                key={`${item.start}-${item.end}`}
            />
        ));
    }

    if (transcript.length === 0) return (
        <div className="flex-1 flex items-center justify-center">
            <p className="text-sm">No transcript yet. Hit the button below to start recording!</p>
        </div>
    );

    return (
        <div ref={containerRef} className="flex-1 overflow-auto flex flex-col gap-[2px] p-[20px] pb-[100px]">
            {renderTranscript()}
            {isRecording && (
                <div className="flex gap-[10px] items-center rounded-lg">
                    <p className="text-sm min-w-[60px] text-theme-1">
                        {formatTime(Date.now() - (transcript[transcript.length - 1]?.timestamp || Date.now()))}
                    </p>
                    <div className="flex items-center gap-2">
                        <Loader2 className="size-3.5 animate-spin text-theme-link" />
                        <p className="text-sm text-theme-1 italic">Transcribing...</p>
                    </div>
                </div>
            )}
        </div>
    );
}
*/

export const TranscriptTab = () => {

    return (
        <Content />
    )
}

const Content = () => {
    const { transcript, noteId, setTranscript, liveTranscript, isRecording } = useNotes();
    const containerRef = useRef<HTMLDivElement>(null);
    const { authenticatedClient } = useCommunication();

    useEffect(() => {
        if (transcript.length > 0) return;

        const getTranscriptContent = async () => {
            const client = await authenticatedClient();
            if (!client) {
                console.error("Error creating auth client:");
                return;
            }

            const { data, error } = await client.from('transcripts').select('content').eq('note_id', noteId).single();

            if (error) {
                console.error("Error fetching transcript:", error);
                return null;
            }

            setTranscript(data.content);
        }

        getTranscriptContent();
    }, [noteId]);

    // Auto-scroll when live transcript updates
    useEffect(() => {
        if (containerRef.current && liveTranscript) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [liveTranscript]);

    // Combine transcript chunks into complete sentences
    const getCombinedTranscript = () => {
        const combined: any[] = [];
        let currentChunk = {
            text: '',
            start: 0,
            end: 0,
            cumulativeTime: 0,
            isLive: false
        };
        let cumulativeTime = 0;

        // Process backend transcript
        transcript.forEach((item: any, index: number) => {
            // Filter out noise phrases that transcription service incorrectly detects
            const text = item.text?.trim() || '';

            // Skip if the text is EXACTLY a period or "Thank you."
            if (text === '.' || text === 'Thank you.') {
                return;
            }

            // Initialize first chunk
            if (currentChunk.text === '') {
                currentChunk.start = item.start || 0;
                currentChunk.cumulativeTime = cumulativeTime;
            }

            // Add text to current chunk
            currentChunk.text += (currentChunk.text ? ' ' : '') + text;
            currentChunk.end = item.end || 0;
            cumulativeTime += (item.end || 0) * 1000;

            // Check if this ends a sentence (has period, question mark, or exclamation)
            const endsWithPunctuation = /[.!?]$/.test(text);
            const isLastItem = index === transcript.length - 1;

            if (endsWithPunctuation || isLastItem) {
                // Complete the current chunk
                combined.push({ ...currentChunk });
                // Reset for next chunk
                currentChunk = {
                    text: '',
                    start: 0,
                    end: 0,
                    cumulativeTime: cumulativeTime,
                    isLive: false
                };
            }
        });

        // Add live transcript as the last item if recording and available
        if (isRecording && liveTranscript) {
            combined.push({
                text: liveTranscript,
                start: 0,
                end: 0,
                cumulativeTime: cumulativeTime,
                isLive: true
            });
        }

        return combined;
    };

    const combinedTranscript = getCombinedTranscript();

    if (transcript.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center p-4 h-full">
                <div className="flex flex-col items-center gap-2">
                    <AudioLines className="size-8 mx-auto text-theme-1" />
                    <p className="text-xs text-theme-1">Start recording to generate a transcript</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[640px] mx-auto px-4 py-8">
            {/* Transcript items */}
            <div className="space-y-1" ref={containerRef}>
                {combinedTranscript.map((item: any, index: number) => {
                    // Use stable key that doesn't change when live text updates
                    const key = item.isLive ? 'live-transcript' : `transcript-${index}`;

                    return (
                        <div
                            key={key}
                            className="group flex items-start gap-3 rounded-lg hover:bg-theme-1/30 transition-all duration-200"
                        >
                            {/* Timestamp and text on same line */}
                            <span className="text-[10px] font-mono font-bold text-theme-1 shrink-0 mt-0.5">
                                {formatTime(item.cumulativeTime)}
                            </span>
                            <p className="text-xs leading-relaxed text-theme-0 font-normal">
                                {item.text}
                            </p>
                        </div>
                    );
                })}

                {isRecording && (
                    <div className="flex gap-3 items-center rounded-lg">
                        <span className="text-[10px] font-mono font-bold text-theme-1 shrink-0 mt-0.5">
                            {formatTime((combinedTranscript[combinedTranscript.length - 1]?.cumulativeTime || 0) + 1000)}
                        </span>
                        <p className="text-xs leading-relaxed text-theme-0 font-normal">
                            <Loader2 className="size-3.5 animate-spin text-theme-2" />
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

